import { useEffect, useRef, useState } from "react";
import { motion, useAnimationControls, useReducedMotion } from "framer-motion";
import useDesktopPointer from "../hooks/useDesktopPointer";
import "../styles/flowMarkSlider.css";
import { MOTION_SPRING } from "../../../shared/constants/motion";

export default function FlowMarkSlider({ value, max, onChange, label }) {
  const interactive = useDesktopPointer();
  const reducedMotion = useReducedMotion();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dragging, setDragging] = useState(false);
  const detent = useAnimationControls();
  const empty = value === "" || value == null;
  const numericValue = empty ? 0 : Math.min(max, Math.max(0, Number(value) || 0));
  const engaged = interactive && (hovered || focused || dragging);
  const held = useRef(false);
  const lastMark = useRef(empty ? null : numericValue);

  useEffect(() => { lastMark.current = empty ? null : numericValue; }, [empty, numericValue]);

  useEffect(() => {
    if (!interactive) {
      held.current = false;
      setHovered(false);
      setFocused(false);
      setDragging(false);
    }
  }, [interactive]);

  const commit = (rawValue) => {
    if (!interactive) return;
    const next = Math.min(max, Math.max(0, Math.round(rawValue)));
    if (!Number.isFinite(next) || next === lastMark.current) return;
    lastMark.current = next;
    onChange(String(next));
    if (!reducedMotion) {
      detent.start({
        scaleX: [1, 0.88, 1],
        scaleY: [1, 1.3, 1],
        y: [0, -1.5, 0],
        transition: { duration: 0.14 },
      });
    }
  };

  const move = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    if (bounds.width > 0) commit((event.clientX - bounds.left) / bounds.width * max);
  };
  const release = () => { held.current = false; setDragging(false); };

  return (
    <div
      className={`mark-slider${engaged ? " is-engaged" : ""}${dragging ? " is-dragging" : ""}`}
      onPointerEnter={() => interactive && setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      <motion.div className="mark-slider-rail" aria-hidden="true" animate={{ scaleY: engaged && !reducedMotion ? 1.65 : 1 }} transition={MOTION_SPRING.pop}>
        <span className="mark-slider-fill" />
      </motion.div>
      <motion.span className="mark-slider-thumb" aria-hidden="true" style={{ left: `${max > 0 ? numericValue / max * 100 : 0}%` }} animate={{ opacity: engaged ? 1 : 0, scale: engaged ? 1 : 0.6 }} transition={{ duration: reducedMotion ? 0 : 0.16 }}>
        <motion.span animate={detent} />
      </motion.span>
      <input
        className="mark-slider-input"
        type="range"
        min={0}
        max={max}
        step={1}
        value={Math.round(numericValue)}
        aria-label={`${label} slider`}
        aria-valuetext={`${numericValue} out of ${max} marks`}
        onChange={(event) => commit(event.currentTarget.valueAsNumber)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onPointerDown={(event) => {
          if (!interactive || event.button !== 0 || event.pointerType === "touch") return;
          event.preventDefault();
          held.current = true;
          setDragging(true);
          event.currentTarget.focus({ preventScroll: true });
          event.currentTarget.setPointerCapture?.(event.pointerId);
          move(event);
        }}
        onPointerMove={(event) => {
          if (held.current) move(event);
        }}
        onPointerUp={(event) => {
          if (held.current) move(event);
          release();
          if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
        }}
        onPointerCancel={release}
        onLostPointerCapture={release}
        onKeyDown={(event) => {
          if (empty && ["Home", "ArrowLeft", "ArrowDown"].includes(event.key)) {
            event.preventDefault();
            commit(0);
          }
        }}
      />
    </div>
  );
}
